/** Pinia Store para la gestión de Carátula */
import { defineStore } from 'pinia';
import { getCurrentInstance } from 'vue';
import { useToast } from 'vue-toast-notification';
import { useLoadingStore } from '@/pinia/loading.module';
import { useAuthStore } from '@/pinia/auth.module';
import { getLocaleMessage } from '@/services/MessagesService';
import { getNestedValue } from '@/helpers/sort';
import * as service from '@/services';
import html2pdf from 'html2pdf.js';

const $toast = useToast();
const endPointCover = "/api/covePage";

export const useContractCoverStore = defineStore('contractCover', {
    /**
     * Estado central del store.
     */
    state: () => ({
        /**Modal */
        isEdit: false,
        isModalOpen: false,
        loanApplicationsOptions: [],
        selectedApplication: null,
        contractContent: null,
        errors: null,
        /**Formulario */
        form: {},
        nameFilePDF: "Carátula ANEXO A",
        /**Datos de tabla */
        sortKey: '',
        sortOrder: 1, // 1 for ascending, -1 for descending
        itemsPerPage: 10, //Por default 10 por pagina
        currentPage: 1,
        searchString: '',
        coverPages: []
    }),
    /**
     * Getters para filtrar, ordenar y paginar las carpatulas.
     */
    getters: {
        /**
         * Devuelve las carátulas filtradas por el texto de búsqueda.
         */
        filteredS: (state) => {
            if (!state.searchString) return state.coverPages;

            //#region control de fecha string
            const instance = getCurrentInstance();
            const $filters = instance?.appContext.config.globalProperties.$filters;
            //#endregion control de fecha string

            const searchLower = state.searchString.toLowerCase();
            return state.coverPages.filter(cover => {
                const firstNameProspect = cover?.prospects?.generalInformation?.firstName ? cover?.prospects?.generalInformation?.firstName?.toLowerCase() : '';
                const lastNameProspect = cover?.prospects?.generalInformation?.lastName ? cover?.prospects?.generalInformation?.lastName?.toLowerCase() : '';
                const mothersMaidenNameProspect = cover?.prospects?.generalInformation?.mothersMaidenName ? cover?.prospects?.generalInformation?.mothersMaidenName?.toLowerCase() : '';
                const nameProspect = cover?.prospects?.generalInformation?.businessName === null ?
                    `${firstNameProspect} ${lastNameProspect} ${mothersMaidenNameProspect}`
                    :
                    cover?.prospects?.generalInformation?.businessName ? cover?.prospects?.generalInformation?.businessName?.toLowerCase() : '';

                return (
                    cover?.coverPage?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    cover?.loanApplications?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    cover?.prospects?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    cover?.products?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    cover?.products?.productContract?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    cover?.products?.productType?.name?.toLowerCase().includes(searchLower) ||
                    nameProspect.includes(searchLower) ||
                    cover.prospects?.personTypes?.name?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(cover?.coverPage.createdAt)?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(cover?.coverPage?.updatedAt)?.toLowerCase().includes(searchLower)
                );
            });
        },
        /**
         * Ordena las carátula según la clave de orden actual.
         */
        sortedS: (state) => {
            // Logica de ordenamiento
            let sortedList = [...state.filteredS];
            if (state.sortKey) {
                sortedList.sort((a, b) => {

                    // Caso especial para el nombre
                    if (state.sortKey === 'name') {
                        const getNameForSort = (item) => {
                            if (!item) return '';

                            if (item.prospects.generalInformation.businessName === null) {
                                return `${item.prospects?.generalInformation?.firstName || ''} ${item.prospects?.generalInformation?.lastName || ''} ${item.prospects?.generalInformation?.mothersMaidenName || ''}`.toLowerCase();
                            } else {
                                return (item.prospects?.generalInformation?.businessName || '').toLowerCase();
                            }
                        };

                        const aName = getNameForSort(a);
                        const bName = getNameForSort(b);
                        if (aName < bName) return -1 * state.sortOrder;
                        if (aName > bName) return 1 * state.sortOrder;
                        return 0;
                    }

                    // Ordenamiento normal para otras columnas
                    let aValue = getNestedValue(a, state.sortKey);
                    let bValue = getNestedValue(b, state.sortKey);

                    if (aValue < bValue) return -1 * state.sortOrder;
                    if (aValue > bValue) return 1 * state.sortOrder;
                    return 0;
                });
            }
            return sortedList;
        },
        /**
         * Aplica paginación a la lista ordenada.
         */
        paginatedS: (state) => {
            if (!state.sortedS || state.sortedS.length === 0) return [];
            let start = (state.currentPage * state.itemsPerPage) - state.itemsPerPage;
            return state.sortedS.slice(start, start + state.itemsPerPage);
        }
    },
    /**
     * Acciones asincrónicas y mutaciones del store.
     */
    actions: {
        /**
         * Obtiene las opciones de solicitudes disponibles para llenar el combo de selección de la modal de carátula.
         * http://localhost:5120/api/covePage/loanApplications/1 ${userAuth.user.companyId}
         */
        async filterLoanApplications() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.coverPageApi.get(`${endPointCover}/loanApplications/${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.loanApplicationsOptions = data.data;
                }
                useLoadingStore().loading = false;
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Crea un nuevo regisro de carátula.
         * @param {*} payload 
         * @returns 
         * http://localhost:5145/api/coverPage/branch/{branchOfficeId}/company/{companyId}
        */
        async createCoverPage(payload) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.coverPageApi.post(`${endPointCover}/branch/${this.form.branchOffices.id}/company/${useAuthStore().user.companyId}`, payload);
                if (data && data.status) {
                    $toast.success(getLocaleMessage('coverPage', 'creatingSuccess'));
                    this.form.coverPage = data.data;
                    this.selectedApplication.coverPage = data.data;
                    this.coverPages.unshift(this.selectedApplication);
                    return data;
                } else {
                    $toast.success(getLocaleMessage('coverPage', 'creatingError'));
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Obtiene todos las carátulas registrados.
         *  http://localhost:5120/api/covePage/full/details/company/1
        */
        async fetchCoverPage() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.coverPageApi.get(`${endPointCover}/full/details/company/${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.coverPages = data.data;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Actualiza un registro de carátula existente.
         * http://localhost:5145/api/coverPage
        */
        async updateCoverPage(payload) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.coverPageApi.put(`${endPointCover}`, payload);
                if (data && data.status) {
                    const index = this.coverPages.findIndex(c => c.coverPage.id === data.id);
                    if (index !== -1) {
                        this.coverPages.splice(index, 1, this.form);
                    }
                    $toast.success(getLocaleMessage('coverPage', 'editingSuccess'));
                    return data;
                } else {
                    $toast.success(getLocaleMessage('coverPage', 'editingError'));
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Abre el modal para crear nuevo registro de carátula.
         * Acción por el boton Generar
         * Abrir modal de creación isEdit = false
         */
        async openModal() {
            this.isModalOpen = true;
            this.isEdit = false;
            await this.filterLoanApplications(); //Refrescar los datos del combo de selección
        },
        /**
         * Cierra el modal y limpia estados relacionados.
         */
        async closeModal() {
            this.isEdit = false;
            this.isModalOpen = false;
            this.loanApplicationsOptions = [];
            this.selectedApplication = null;
            this.contractContent = null;
            this.errors = null;
            this.form = {};
            await this.fetchCoverPage(); //Refrescar datos de las tablas
        },
        /**
         * Maneja errores en la comunicación con la API.
         */
        handleError(error) {
            this.errors = error;
            if (error.response) {
                console.log("Inspec handle ERROR IF: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'responseError');
                $toast.error(msg);
            } else if (error.request) {
                console.log("Inspec handle ERROR ELSE IF: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'networkError');
                $toast.error(msg);
            } else {
                console.log("Inspec handle ERROR ELSE: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'generalError');
                $toast.error(msg);
            }
        },
        /**
        * Cambia el orden de las columnas en la tabla.
        */
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortOrder *= -1;
            } else {
                this.sortOrder = 1;
            }
            this.sortKey = key;
            this.currentPage = 1; // Resetear a la primera página al ordenar
        },
        /**
         * Establece la página actual del paginador.
         */
        setCurrentPage(page) {
            this.currentPage = page;
        },
        /**
         * Carga los datos de un carátula existente para edición.
         * Abrir modal de edición isEdit = true
         */
        editData(coverPage) {
            this.isEdit = true;
            this.selectedApplication = coverPage;
            this.form = coverPage;
        },
        /**
         * Acción para guarda los datos del formulario, ya sea creando o actualizando.
        */
        async saveData() {
            try {
                useLoadingStore().loading = true;
                let payload = {
                    id: null,
                    loanApplicationId: this.form.loanApplications.id,
                    statusId: 21,
                    referenceNumber: ''
                };

                let response;
                if (this.isEdit) {
                    payload.id = this.form.coverPage.id;
                    payload.referenceNumber = this.form.coverPage.referenceNumber;
                    response = await this.updateCoverPage(payload);

                } else {
                    response = await this.createCoverPage(payload);
                }
                
                useLoadingStore().loading = true;

                if (response && response.status) {
                    this.downloadPDF();
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Funcionalidad para manejar la descarga de un pdf
         * @returns Descarga un PDF
         */
        async downloadPDF() {
            if (!this.contractContent) return;

            /**Ocultar el boton de descarga */
            const formActions = document.querySelector('.form-actions');
            if (formActions) formActions.style.display = 'none';

            // Elemento a convertir
            const element = document.getElementById('content-to-pdf');
            if (element) {
                element.classList.add('pdf-mode');
                element.classList.remove('web-container-mode');
            }

            //const table = document.querySelector('.table-cover'); // Usa querySelector para el primer elemento
            //if (table) table.classList.remove('web-mode');

            const tables = document.querySelectorAll('.table-cover'); // Retorna una NodeList
            if (tables) tables.forEach(table => table.classList.remove('web-mode'));

            const opt = {
                margin: 10,
                filename: this.nameFilePDF,
                image: { type: 'jpeg', quality: 1 },
                html2canvas: {
                    scale: 2,
                    logging: true,
                    useCORS: true,
                    letterRendering: true,
                    clip: false, // Permite capturar contenido fuera de los límites del viewport
                    ignoreElements: function (element) {
                        return false; // Captura todos los elementos
                    }
                },
                jsPDF: {
                    unit: 'mm',
                    format: 'a4', // Tamaño A4 en píxeles
                    orientation: 'portrait'
                },
                // Configuración para manejar saltos de página
                pagebreak: {
                    mode: ['avoid-all', 'css', 'legacy']
                }
            };

            try {
                useLoadingStore().loading = true;
                await html2pdf().set(opt).from(this.contractContent).save();
            } catch (error) {
                useLoadingStore().loading = false;
                this.handleError(error);
            } finally {
                useLoadingStore().loading = false;

                /**Restableciendo diseños de origen en la modal */
                if (formActions) formActions.style.display = 'block';

                if (element) {
                    element.classList.remove('pdf-mode');
                    element.classList.add('web-container-mode');
                };
                // table.classList.add('web-mode');
                if (tables) tables.forEach(table => table.classList.add('web-mode'));
            }

            /**
             * Nota: Con esta parte del codigo soluciono el detalle de cuando se mantiene
             * abierto la modal de creación despues dar descargar y este se manda a GUARDAR 
             * si el usuario quiere modificar algun campo y mandar descargar de nuevo con la
             * nueva captura en alguno de los campos no deja porque le marca error de solicitud
             * ya existente.
             * Opción 1:
             * Comportamiento: Una vez creado el registro y como se mantiene la modal abierta
             * por automatico cuando se termina de GUARDAR el registro en la BD este se convierte
             * en una modal de edición. Si ese es el comportamiento adecuado dejar este codigo
             * Opción 2:
             * Comportamiento: Una vez creado el registro y como se mantiene la modal abierta
             * y sigue siendo una creación este al querer editar un dato y de descargar lo sigue
             * reconociendo como una creación y marca error porque dice que ya existe el uso
             * de esa solicitud dentro del proceso de aviso de privacidad. Si ese es el
             * comportamiento adecuado retirar este codigo
             */
            //this.isEdit = true; //indica vuelvelo edición
            //this.policyId = this.privacyPolicies[0].id; //setea el valor del nuevo registro creado
            //this.numReference = this.privacyPolicies[0].loanApplication.referenceNumber; //setea el valor de numero de solicitud
        },
        setContractContent(ref) {
            this.contractContent = ref;
        }
    }
});